import {
  enhancedPricePresentationConfigFromJSON,
  IEnhancedPricePresentationConfigData,
  IEnhancedPricePresentationConfigRecord,
} from "common/records/EnhancedPricePresentationConfig";
import { IJobMenuData, IJobMenuRecord, jobMenuFromJSON } from "common/records/JobMenu";
import {
  IJobResultingConfigData,
  jobResultingConfigFromJSON,
  JobResultingConfigRecord,
} from "common/records/JobResultingConfig";
import { Nullable } from "common/types";
import { fromJS, Record } from "immutable";

/** Which document family a custom document-order layout belongs to. */
export type LayoutKind = "proposal" | "agreement";

export const preferenceConfigFromJSON = (json: Partial<IPreferenceConfigData>): PreferenceConfigRecord => {
  const data: IPreferenceConfigRecord = { ...(json as any) };

  if (json.report_emails) {
    data.report_emails = fromJS(json.report_emails);
  }

  if (json.job_menu) {
    data.job_menu = jobMenuFromJSON(json.job_menu);
  }

  if (json.signatures) {
    data.signatures = fromJS(json.signatures);
  }

  if (json.cronofy) {
    data.cronofy = fromJS(json.cronofy);
  }

  if (json.layout) {
    data.layout = fromJS(json.layout);
  }

  if (json.screen_share) {
    data.screen_share = fromJS(json.screen_share);
  }

  if (json.billing) {
    data.billing = fromJS(json.billing);
  }

  if (json.cover_page) {
    data.cover_page = fromJS(json.cover_page);
  }

  if (json.document_signed_notification) {
    data.document_signed_notification = fromJS(json.document_signed_notification);
  }

  if (json.titles) {
    data.titles = fromJS(json.titles);
  }

  if (json.integrations) {
    data.integrations = fromJS(json.integrations);
  }

  if (json.document_order) {
    data.document_order = fromJS(json.document_order);
  }

  if (json.photos) {
    data.photos = fromJS(json.photos);
  }

  if (json.estimator) {
    data.estimator = fromJS(json.estimator);
  }

  // fromJSON hydrates inspection to a Map (never leaving it null) so the labels-config form's `setIn`
  // can traverse the node; the record default stays null like its sibling config keys.
  data.inspection = fromJS(json.inspection || {});

  if (json.send_email) {
    data.send_email = fromJS(json.send_email);
  }

  if (json.measurement) {
    data.measurement = fromJS(json.measurement);
  }

  if (json.sales_tax) {
    data.sales_tax = fromJS(json.sales_tax);
  }

  if (json.marketsharp) {
    data.marketsharp = fromJS(json.marketsharp);
  }

  if (json.timezone) {
    data.timezone = fromJS(json.timezone);
  }

  if (json.signed_documents) {
    data.signed_documents = fromJS(json.signed_documents);
  }

  if (json.link_expiration) {
    data.link_expiration = fromJS(json.link_expiration);
  }

  data.finance_app = fromJS(json.finance_app || {});

  if (json.notes === undefined) {
    json.notes = {
      enable_editing: true,
    };
  }

  if (json.notes) {
    data.notes = fromJS(json.notes);
  }

  data.price_presentation = fromJS(json.price_presentation || {});

  data.presentations = fromJS(json.presentations || {});

  data.enhanced_price_presentation = enhancedPricePresentationConfigFromJSON(json.enhanced_price_presentation || {});

  data.markup = fromJS(json.markup || {});

  if (json.job_resulting) {
    data.job_resulting = jobResultingConfigFromJSON(json.job_resulting);
  }

  return new PreferenceConfigRecord(data);
};

export interface IPreferenceConfigData {
  report_emails: any;
  job_menu: IJobMenuData;
  signatures: any;
  cronofy: any;
  layout: any;
  screen_share: any;
  billing: any;
  cover_page: any;
  document_signed_notification: any;
  titles: any;
  integrations: any;
  document_order: any;
  photos: any;
  estimator: any;
  inspection: any;
  send_email: any;
  measurement: any;
  sales_tax: any;
  link_expiration: any;
  marketsharp: any;
  price_presentation: any;
  markup: any;
  timezone: any;
  signed_documents: any;
  notes: any;
  presentations: any;
  enhanced_price_presentation: Partial<IEnhancedPricePresentationConfigData>;
  job_resulting: Partial<IJobResultingConfigData>;
  finance_app: any;
}

export interface IPreferenceConfigRecord {
  report_emails: any;
  job_menu: IJobMenuRecord;
  signatures: any;
  cronofy: any;
  layout: any;
  screen_share: any;
  billing: any;
  cover_page: any;
  document_signed_notification: any;
  titles: any;
  integrations: any;
  document_order: any;
  photos: any;
  estimator: any;
  inspection: any;
  send_email: any;
  measurement: any;
  sales_tax: any;
  link_expiration: any;
  marketsharp: any;
  price_presentation: any;
  markup: any;
  timezone: any;
  signed_documents: any;
  notes: any;
  presentations: any;
  enhanced_price_presentation: IEnhancedPricePresentationConfigRecord;
  job_resulting: JobResultingConfigRecord;
  finance_app: any;
}

export const defaultPreferenceConfigProps: IPreferenceConfigRecord = {
  report_emails: null,
  job_menu: jobMenuFromJSON({}),
  signatures: null,
  cronofy: null,
  layout: null,
  screen_share: null,
  billing: null,
  cover_page: null,
  document_signed_notification: null,
  titles: null,
  integrations: null,
  document_order: null,
  photos: null,
  estimator: null,
  inspection: null,
  send_email: null,
  measurement: null,
  sales_tax: null,
  link_expiration: null,
  marketsharp: null,
  price_presentation: null,
  markup: null,
  timezone: null,
  signed_documents: null,
  notes: null,
  presentations: null,
  enhanced_price_presentation: enhancedPricePresentationConfigFromJSON({}),
  job_resulting: jobResultingConfigFromJSON({}),
  finance_app: null,
};

export class PreferenceConfigRecord extends Record(defaultPreferenceConfigProps) implements IPreferenceConfigRecord {}
